import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { OPERATION } from '@/constants';
import { compareObjectsDeeply } from './utils';

dayjs.extend(utc);

export const DATE_FORMATS = Object.freeze({
  STANDARD: 'YYYY-MM-DD', // Backend format
  STANDARD_WITH_TIME: 'YYYY-MM-DD HH:mm', // Backend format with time
  DISPLAY: 'DD-MM-YYYY', // User-friendly format
  DISPLAY_WITH_TIME: 'DD-MM-YYYY HH:mm', // User-friendly with time
  FULL_MONTH: 'MMMM D, YYYY', // Example: "January 31, 2025"
  SHORT_MONTH: 'MMM D, YYYY', // Example: "Jan 31, 2025"
  US_FORMAT: 'MM/DD/YYYY', // Example: "01/31/2025"
  US_FORMAT_WITH_TIME: 'MM/DD/YYYY hh:mm A', // Example: "01/31/2025 02:30 PM"
  ISO: 'YYYY-MM-DDTHH:mm:ss[Z]', // Example: "2025-01-31T14:30:00Z"
  TIME_ONLY: 'HH:mm', // Example: "14:30"
  DAY_ONLY: 'DD', // Example: "01", "02", "09", "31"
  YEAR_MONTH: 'YYYY-MM', // Example: "2025-02"
  MONTH_YEAR: 'MM-YYYY', // Example: "02-2025"
  DISPLAY_DOTS: 'DD.MM.YYYY.', // User-friendly with dots
});

// Public holidays are NOT duplicated on the frontend — the backend is the
// single source of truth and exposes `company.publicHolidays` (an array of
// `YYYY-MM-DD` strings) as part of the company response. Date-picker / leave
// helpers below take that list as an explicit argument so they stay pure and
// market-agnostic.

/**
 * Formats a date value for sending to the backend.
 * Ensures the date is valid before formatting.
 *
 * @param {string|Date|dayjs.Dayjs} value - The date value to be formatted.
 * @param dateFormat
 * @returns {string|undefined} The formatted date string in "YYYY-MM-DD HH:mm" format, or undefined if the value is invalid.
 */
export const formatDateForBackend = (
  value,
  dateFormat = DATE_FORMATS.STANDARD_WITH_TIME
) => {
  if (!value || !dayjs(value).isValid()) return null;

  return dayjs(value).format(dateFormat);
};

/**
 * Parses a date string or object for use in a date input field.
 * Ensures the input is a valid date before parsing.
 * Uses UTC for STANDARD format to prevent timezone issues with date-only values.
 *
 * @param {string|Date} formDate - The date value to be parsed.
 * @param {string} [dateFormat='DD-MM-YYYY'] - The expected format of the input date.
 * @returns {dayjs.Dayjs|undefined} A Dayjs object representing the parsed date, or undefined if the input is invalid.
 */
export const parseDateForInput = (
  formDate,
  dateFormat = DATE_FORMATS.DISPLAY
) => {
  if (!formDate) return undefined;

  if (dateFormat === DATE_FORMATS.STANDARD) {
    const parsed = dayjs.utc(formDate);
    if (!parsed.isValid()) return undefined;

    return dayjs(parsed.format(DATE_FORMATS.STANDARD));
  }

  const parsed = dayjs(formDate, dateFormat);
  if (!parsed.isValid()) return undefined;
  return parsed;
};

/**
 * Converts a date string to a dayjs object.
 * Ensures the input is a valid date before conversion.
 * @param {string} dateString - Date in DATE_FORMATS.STANDARD format.
 * @returns {dayjs.Dayjs|undefined} - Dayjs object, or undefined if the date is invalid.
 */
export const parseDate = (dateString) => {
  const parsedDate = dayjs(dateString);
  return parsedDate.isValid() ? parsedDate : undefined;
};

/**
 * Formats a time value for sending to the backend.
 * Ensures the time is valid before formatting.
 *
 * @param {string|Date|dayjs.Dayjs} value - The time value to be formatted.
 * @param {string} [timeFormat='HH:mm'] - The format to apply.
 * @returns {string|undefined} The formatted time string in "HH:mm" format, or undefined if the value is invalid.
 */
export const formatTimeForBackend = (
  value,
  timeFormat = DATE_FORMATS.TIME_ONLY
) => {
  const parsedTime = dayjs(value, DATE_FORMATS.TIME_ONLY);
  return parsedTime.isValid() ? parsedTime.format(timeFormat) : value;
};

/**
 * Parses a time string into a dayjs object.
 * Ensures the time input is valid before parsing.
 *
 * @param {string} timeString - The time value to be parsed in "HH:mm" format.
 * @returns {dayjs.Dayjs|undefined} A Dayjs object representing the parsed time, or undefined if the input is invalid.
 */
export const parseTimeForInput = (timeString) => {
  const parsedTime = dayjs(timeString, DATE_FORMATS.TIME_ONLY);
  return parsedTime.isValid() ? parsedTime : undefined;
};

/**
 * Formats a dayjs object or Date into a string.
 * Ensures the input date is valid before formatting.
 * @param {Date | dayjs.Dayjs | string} date - The date to format.
 * @param {string} format - The format to apply.
 * @returns {string|undefined} - Formatted date string, or undefined if the date is invalid.
 */
export const formatDate = (date, format = DATE_FORMATS.STANDARD) => {
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format(format) : undefined;
};

/**
 * Converts year and month string values to a Dayjs object for the first day of the month.
 *
 * @param {string} year - The year as a string.
 * @param {string} month - The month as a string (e.g., '1' for January, '12' for December).
 * @returns {dayjs.Dayjs} - A Dayjs object representing the first day of the given month and year.
 */
export const transformToFirstDayOfMonth = (year, month) => {
  const formattedDate = `${year}-${month.padStart(2, '0')}-01`;
  return dayjs(formattedDate);
};

/**
 * Returns the weekdays ordered with the first day of the week moved to the end.
 * Transforms all weekdays to lowercase.
 *
 * @returns {string[]} An array of weekdays in lowercase, with the first day moved to the end.
 */
export const getWeekdaysOrdered = () => {
  // Get the weekdays from dayjs and shift the first day of the week to the end
  let days = dayjs.localeData().weekdays();
  let weekDays = [...days.slice(1), days[0]];

  // Map the weekdays to lowercase
  return weekDays.map((day) => day.toLowerCase());
};

/**
 * Disables dates that do not belong to the selected month.
 *
 * @param {dayjs.Dayjs} calendarValue - The currently selected date in the calendar.
 * @param {dayjs.Dayjs} targetDate - The target date to be checked.
 * @returns {boolean} - Returns true if the target date is not in the same month as the calendarValue.
 */
export const isDateDisabled = (calendarValue, targetDate) => {
  return calendarValue.month() !== targetDate.month();
};

/**
 * Checks if a target date is selected in the calendar.
 *
 * @param {Array} hoursByDay - Array of selected date objects with a 'hoursByDay' property.
 * @param {Date | dayjs.Dayjs | string} targetDate - The date to check against the selected dates.
 * @returns {boolean} - Returns true if the target date is selected, otherwise false.
 */
export const isCalendarCellActive = (hoursByDay, targetDate) => {
  return hoursByDay?.some(({ selectedDate, isSelected }) => {
    return selectedDate === formatDate(targetDate) && isSelected;
  });
};

/**
 * Compares the current query parameters with the default parameters to detect any changes.
 * Performs a deep comparison using JSON.stringify to check if values have been modified.
 *
 * @param {Object} queryParams - The current query parameters object (e.g., from URL or state).
 * @param {Object} defaultParams - The default parameters object to compare against.
 * @returns {boolean} Returns true if any parameter differs from its default value, otherwise false.
 *
 * @example
 * const defaultParams = { offset: 0, limit: 15, status: undefined };
 * const queryParams = { offset: 0, limit: 15, status: 'active' };
 * const isChanged = isFormDataModified(queryParams, defaultParams);
 * console.info(isChanged); // true
 *
 * @example
 * const defaultParams = { offset: 0, limit: 15 };
 * const queryParams = { offset: 0, limit: 15 };
 * const isChanged = isFormDataModified(queryParams, defaultParams);
 * console.info(isChanged); // false
 */
export const isFormDataModified = (queryParams, defaultParams) => {
  // Safety check: return false if either param is null/undefined
  if (!defaultParams || !queryParams) {
    return false;
  }

  const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const keys = Object.keys(defaultParams);

  for (const key of keys) {
    if (!deepEqual(queryParams?.[key], defaultParams[key])) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if all properties and values in the `target` object are present and match in the `source` object.
 * Performs a deep comparison for nested objects.
 *
 * @param {Object} source - The object to be checked for matching properties and values.
 * @param {Object} target - The object containing properties and values to match against the source.
 * @returns {boolean} Returns `true` if all properties and values in `target` are present and match in `source`, otherwise `false`.
 */
export const isModifiedFromDefaults = (source, target) => {
  for (const key in target) {
    const val1 = source[key];
    const val2 = target[key];

    const areValuesObjects =
      typeof val1 === 'object' &&
      typeof val2 === 'object' &&
      val1 !== null &&
      val2 !== null;

    if (
      (areValuesObjects && !compareObjectsDeeply(val1, val2)) ||
      (!areValuesObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Converts a path (either a string or array of strings) into a nested object.
 *
 * @param {string | string[]} path - The path to set in the form (either dot notation string or array of keys).
 * @param {any} value - The value to set at the specified path.
 * @returns {Object} - The nested object corresponding to the path.
 */
export const convertToObject = (path, value) => {
  let keys = [];

  // If the path is a string, split it into an array of keys
  if (typeof path === 'string') {
    keys = path.split('.');
  } else if (Array.isArray(path)) {
    keys = path;
  } else {
    throw new Error('Path must be a string or an array of strings');
  }

  return keys.reduceRight((acc, key) => ({ [key]: acc }), value);
};

/**
 * Converts an ARGB or hex-like string into a valid CSS hex color.
 *
 * If the input is in ARGB format (e.g., 'FF000000'), it strips the alpha channel
 * and returns the RGB part as a hex color (e.g., '#000000').
 * If the input is the string 'transparent', it returns it directly.
 * If the input is invalid or shorter than expected, it attempts to sanitize and fallback.
 *
 * @param {string} argb - A color string in ARGB (e.g., 'FF00FF00') or hex format (e.g., '00ff00').
 * @returns {string} A valid CSS color string (e.g., '#00ff00' or 'transparent').
 */
export const formatHexColor = (argb = 'FF000000') => {
  if (argb === 'transparent') return 'transparent';

  const sanitized = argb.replace(/[^0-9a-fA-F]/g, '');

  if (sanitized.length === 8) {
    return `#${sanitized.slice(2)}`;
  }

  const fallback = sanitized.slice(0, 6);
  return `#${fallback.padEnd(6, 'f')}`;
};

/**
 * Effective "allow weekend & holiday leaves" rule for an employee in a company.
 * AND-semantika: company mora imati flag UKLJUČEN i zaposleni mora biti smenski.
 * Postojeći klijenti sa company.weekendLeaves=true → pre deploy migracija svim
 * zaposlenima setuje shiftWorker=true, klijent kasnije skida pojedinačno.
 */
export const isShiftLeavesEffective = (employee, company) => {
  const companyAllows = !!company?.applicationSettings?.weekendLeaves;
  const employeeIsShift = !!employee?.employmentSettings?.shiftWorker;
  return companyAllows && employeeIsShift;
};

/**
 * Checks if the given date falls on a weekend or a holiday.
 *
 * @param {string | number | Date | dayjs.Dayjs} currentDate - The date to check.
 * @param {boolean} allowWeekendLeaves - If true, weekends are not considered as disabled.
 * @returns {boolean} `true` if the date is a weekend or a holiday, otherwise `false`.
 */
export const isDisabledWeekendAndHoliday = (
  currentDate,
  allowWeekendLeaves = false,
  publicHolidays = []
) => {
  const date = dayjs(currentDate);
  const dayOfWeek = date.day();

  const isWeekend = !allowWeekendLeaves ? (dayOfWeek === 0 || dayOfWeek === 6) : false;
  const isHoliday = !allowWeekendLeaves && publicHolidays.some((holiday) =>
    dayjs(holiday).isSame(date, 'day')
  );

  return isWeekend || isHoliday;
};

/**
 * Calculates the number of selected workdays (excluding weekends and holidays)
 * between two dates. Returns `null` if the date range is not properly selected.
 *
 * @param {Array<string | number | Date>} dateOfLeaveValues - An array containing the start and end dates.
 * @param {boolean} allowWeekendLeaves - If true, weekends are counted as workdays.
 * @returns {number | null} The count of selected workdays, or `null` if the range is invalid.
 */
export const getSelectedWorkdaysCount = (
  dateOfLeaveValues,
  allowWeekendLeaves = false,
  publicHolidays = []
) => {
  if (!dateOfLeaveValues || dateOfLeaveValues.length < 2) {
    return null;
  }

  const startDate = new Date(dateOfLeaveValues[0]);
  const endDate = new Date(dateOfLeaveValues[1]);

  const holidays = publicHolidays.map((date) => new Date(date).toDateString());

  let selectedDays = 0;

  for (
    let date = new Date(startDate);
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    const currentDate = new Date(date);
    const currentDateString = currentDate.toDateString();

    const isWeekend = !allowWeekendLeaves
      ? (currentDate.getDay() === 0 || currentDate.getDay() === 6)
      : false;
    const isHoliday = !allowWeekendLeaves && holidays.includes(currentDateString);

    if (!isWeekend && !isHoliday) {
      selectedDays++;
    }
  }

  return selectedDays;
};

/**
 * Finds the next available workday (excluding weekends and holidays)
 * after the selected date range.
 *
 * @param {Array<string | number | Date>} dateOfLeaveValues - An array containing the start and end dates.
 * @param {boolean} allowWeekendLeaves - If true, weekends are considered as workdays.
 * @returns {Date | null} The next available workday, or `null` if the input is invalid.
 */
export const getNextWorkday = (
  dateOfLeaveValues,
  allowWeekendLeaves = false,
  publicHolidays = []
) => {
  if (!dateOfLeaveValues || dateOfLeaveValues.length < 2) {
    return null;
  }

  const endDate = new Date(dateOfLeaveValues[1]);
  const holidays = publicHolidays.map((date) => new Date(date).toDateString());

  let nextWorkday = new Date(endDate);

  while (true) {
    nextWorkday.setDate(nextWorkday.getDate() + 1);
    const currentDateString = nextWorkday.toDateString();

    const isWeekend = !allowWeekendLeaves
      ? (nextWorkday.getDay() === 0 || nextWorkday.getDay() === 6)
      : false;
    const isHoliday = !allowWeekendLeaves && holidays.includes(currentDateString);

    if (!isWeekend && !isHoliday) {
      break;
    }
  }

  return nextWorkday;
};

/**
 * Determines whether the Employee Request Form should be disabled
 * based on the current action and request status.
 *
 * @param {string} action - The current form action mode (e.g. "edit", "view", "create", etc.)
 * @param {{ status: string } | undefined} data - The employee request data object containing status.
 *                                                If undefined, the form will be considered disabled.
 *
 * @returns {boolean} - Returns `true` if the form should be disabled, otherwise `false`.
 *
 * Logic:
 * - If action is "edit":
 *    → Form is disabled only if status is "ACCEPTED".
 * - If action is "view":
 *    → Form is always disabled.
 * - If data is not present and action is "edit" or "view":
 *    → Form is disabled (to avoid errors).
 * - All other actions:
 *    → Form is enabled.
 */
export const isEmployeeRequestFormDisabled = (action, data) => {
  if ([OPERATION.edit, OPERATION.view].includes(action)) {
    if (!data) return true;

    if (action === OPERATION.edit) {
      return data.status === 'ACCEPTED';
    }

    if (action === OPERATION.view) {
      return true;
    }

    return false;
  }

  return false;
};

/**
 * Removes specific keys from a query string and returns the cleaned query string.
 *
 * @param {string} queryString - The original query string (e.g., "a=1&b=2&c=3").
 * @param {string[]} keysToRemove - An array of keys to remove from the query string.
 * @returns {string} A new query string with the specified keys removed.
 *
 * @example
 * removeQueryParams('a=1&b=2&c=3', ['b', 'c']);
 * // Returns: "a=1"
 */
export const removeQueryParams = (queryString, keysToRemove) => {
  const params = new URLSearchParams(queryString);

  keysToRemove.forEach((key) => {
    params.delete(key);
  });

  return params.toString();
};

/**
 * Converts an array of date values to an array of dayjs objects.
 *
 * @param {Array<string|Date|number>} date - An array of date values to convert.
 * @returns {Array<dayjs.Dayjs>} An array of dayjs objects corresponding to the input dates, or an empty array if input is falsy.
 */
export const getRangePicker = (date) => {
  return date?.map((date) => dayjs(date)) || [];
};

/**
 * Converts a date value to a dayjs object if the date is provided.
 *
 * @param {string|Date|number|null|undefined} date - The date value to convert. Can be a string, Date object, timestamp, or null/undefined.
 * @returns {import('dayjs').Dayjs|undefined} A dayjs object if the date is provided, otherwise undefined.
 */
export const getPickerValue = (date) => {
  return date ? dayjs(date) : undefined;
};

/**
 * Converts a string or an array of strings to uppercase.
 * If the input is an array, returns a new array with all elements in uppercase.
 * If the input is a single string, returns an array containing the uppercase string.
 * If the input is falsy, returns undefined.
 *
 * @param {string|string[]} value - The string or array of strings to convert.
 * @returns {string[]|undefined} An array of uppercase strings, or undefined if input is falsy.
 */
export const extractToUpperCase = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => item.toUpperCase());
  }

  return value ? [value.toUpperCase()] : undefined;
};

/**
 * Converts a string or an array of strings to lowercase.
 * If the input is an array, returns a new array with all elements in lowercase.
 * If the input is a single string, returns an array containing the lowercase string.
 * If the input is falsy, returns undefined.
 *
 * @param {string|string[]} value - The string or array of strings to convert to lowercase.
 * @returns {string[]|undefined} An array of lowercase strings, or undefined if input is falsy.
 */
export const extractToLowerCase = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => item.toLowerCase());
  }

  return value ? [value.toLowerCase()] : undefined;
};

/**
 * Converts the input value to an array if it is not already an array.
 *
 * @param {*} value - The value to convert to an array.
 * @returns {Array|undefined} Returns the value as an array, or undefined if the value is falsy.
 */
export const extractToArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : undefined;
};

/**
 * Retrieves the visibility and disabled state of a form field based on the provided keys and edit state.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.formFields - An object containing form field configurations, keyed by their path.
 * @param {string[]} params.keys - An array of strings representing the path to the field.
 * @param {boolean} params.isEdited - Indicates if the form is in edit mode.
 * @returns {Object} An object containing:
 *   @property {boolean} visible - Whether the field is visible.
 *   @property {boolean} disabled - Whether the field is disabled.
 */
export const getFieldState = ({ formFields, formKeys, isEdited }) => {
  const path = formKeys.join('.');
  const field = formFields?.[path] || {};

  return {
    visible: isEdited ? field.visibility : true,
    disabled: isEdited ? field.disabled : false,
  };
};
